import { Sub as RadixSub } from '@radix-ui/react-dropdown-menu';
import {
  useMemo,
  useContext,
  type ReactNode,
  Children,
  isValidElement,
  cloneElement,
} from 'react';

import { useControllableState } from '../../core/hooks/useControllableState.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { MenuSubContext } from './contexts/MenuSubContext.js';
import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import { useMenuAutoClose } from './hooks/useMenuAutoClose.js';
import { useMenuContext } from './hooks/useMenuContext.js';
import { MobileSub } from './MobileSub.js';
import { SubContent, type MenuSubContentProps } from './SubContent.js';
import { SubTrigger } from './SubTrigger.js';
import { type MenuSubProps } from './types.js';

export type { MenuSubProps };

type SubSlots = {
  trigger: ReactNode | null;
  content: ReactNode[];
  freeform: ReactNode[];
};

/**
 * Iterates over all top-level SubContent nodes and registers tab key handler and behavioral tracking props.
 */
function registerTabHandlers(
  children: ReactNode,
  handleTabKeyPressed: () => void,
  dataDtComponent: string,
): ReactNode {
  return Children.map(children, (child: ReactNode) => {
    return isValidElement<MenuSubContentProps>(child) &&
      child.type === SubContent
      ? cloneElement(
          child,
          _mergeProps(child.props, {
            _onTabKeyPressed: handleTabKeyPressed,
            'data-dt-component': dataDtComponent,
          }),
          child.props.children,
        )
      : child;
  });
}

/** Iterates over child nodes to extract specific component slots. */
function getSubSlots(children: ReactNode) {
  const slots: SubSlots = {
    trigger: null,
    content: [],
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, SubTrigger)) {
      slots.trigger = child;
    } else if (isSlot(child, SubContent)) {
      slots.content.push(child);
    } else {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/**
 * @public
 */
export const Sub = (props: MenuSubProps) => {
  const isMobile = useContext(MobileMenuModeContext);
  const { dataDtComponent } = useMenuContext();

  const {
    defaultOpen: defaultValue,
    open: value,
    onOpenChange: onChange,
    children,
    ...remainingProps
  } = props;
  const [open, updateOpen] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const slots = useMemo(
    () =>
      getSubSlots(
        registerTabHandlers(children, () => updateOpen(false), dataDtComponent),
      ),
    [children, updateOpen, dataDtComponent],
  );

  useMenuAutoClose(open, updateOpen);

  const subContextValue = useMemo(
    () => ({
      isOpen: open ?? false,
      isDefaultOpen: defaultValue ?? false,
      setIsOpen: updateOpen,
    }),
    [open, defaultValue, updateOpen],
  );

  if (isMobile) {
    return <MobileSub {...props} />;
  }

  return (
    <MenuSubContext.Provider value={subContextValue}>
      <RadixSub
        open={open}
        onOpenChange={(isOpen) => updateOpen(isOpen)}
        {...remainingProps}
      >
        {slots.trigger !== null && slots.trigger}
        {open && slots.content}
        {slots.freeform}
      </RadixSub>
    </MenuSubContext.Provider>
  );
};

(Sub as typeof Sub & { displayName: string }).displayName = 'Menu.Sub';
